import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { useApp } from '@/context/AppContext';
import { supabase } from '@/lib/supabase';

export default function ChangePasswordScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { setFlashMessage } = useApp();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');
  const [alertModalOnOk, setAlertModalOnOk] = useState<(() => void) | null>(null);

  const showAlert = (title: string, message: string, onOk?: () => void) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setAlertModalOnOk(onOk ?? (() => setAlertModalVisible(false)));
    setAlertModalVisible(true);
  };

  const handleAlertOk = () => {
    const onOk = alertModalOnOk;
    setAlertModalVisible(false);
    setAlertModalOnOk(null);
    // Defer callback so modal close isn't mixed with navigation state update
    setTimeout(() => onOk?.(), 0);
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showAlert('Error', 'Please fill in all password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert('Error', 'New passwords do not match.');
      return;
    }

    if (newPassword.length < 8 || newPassword.length > 64) {
      showAlert(
        'Error',
        newPassword.length < 8
          ? 'Password must be at least 8 characters.'
          : 'Password must be at most 64 characters.'
      );
      return;
    }

    if (newPassword === currentPassword) {
      showAlert('Error', 'New password must be different from current password.');
      return;
    }

    setLoading(true);

    try {
      if (!user?.email) {
        showAlert('Error', 'Unable to get user information.');
        setLoading(false);
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        showAlert('Error', 'Current password is incorrect.');
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        let errorMessage = 'Unable to update your password. Please try again.';
        if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 8 characters long.';
        } else if (error.message.includes('Weak password')) {
          errorMessage = 'Please choose a stronger password.';
        }
        showAlert('Password update failed', errorMessage);
      } else {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setLoading(false);
        setFlashMessage('passwordUpdated');
        setTimeout(() => router.replace('/(tabs)/settings'), 0);
        return;
      }
    } catch (err) {
      console.error('Error changing password:', err);
      showAlert('Error', 'An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const isFormValid =
    !!currentPassword &&
    !!newPassword &&
    !!confirmPassword &&
    newPassword === confirmPassword &&
    newPassword.length >= 8 &&
    newPassword.length <= 64 &&
    newPassword !== currentPassword;

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: Math.max(insets.top, 16) },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>

          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Ionicons name="lock-closed" size={48} color={Brand.primary} />
            </View>
            <Text style={styles.title}>Change password</Text>
            <Text style={styles.subtitle}>
              Update your account password. You&apos;ll need to enter your current
              password to confirm.
            </Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>Current password</Text>
            <Input
              value={currentPassword}
              onChangeText={setCurrentPassword}
              placeholder="Enter current password"
              secureTextEntry
              autoCapitalize="none"
              style={styles.input}
              editable={!loading}
            />

            <Text style={[styles.label, styles.labelSpaced]}>New password</Text>
            <Input
              value={newPassword}
              onChangeText={setNewPassword}
              placeholder="8-64 characters"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              style={styles.input}
              editable={!loading}
            />

            <Text style={[styles.label, styles.labelSpaced]}>Confirm new password</Text>
            <Input
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Confirm your new password"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              style={styles.input}
              editable={!loading}
            />

            <Button
              onPress={changePassword}
              disabled={!isFormValid || loading}
              style={styles.submitButton}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                'Update password'
              )}
            </Button>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal
        visible={alertModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleAlertOk}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{alertModalTitle}</Text>
            <Text style={styles.modalDescription}>{alertModalMessage}</Text>
            <View style={styles.modalActions}>
              <Button onPress={handleAlertOk} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  header: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  labelSpaced: {
    marginTop: 16,
  },
  input: {
    marginBottom: 0,
  },
  submitButton: {
    marginTop: 24,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: Neutrals.white,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
